/* TYPE DEFINITIONS */
const ViewportOrientations = ["landscape", "portrait"] as const;
type ViewportOrientation = (typeof ViewportOrientations)[number];

type BreakpointMap = {
  mobileSm: number;
  mobile: number;
  tablet: number;
  laptop: number;
  desktop: number;
  widescreen: number;
  ultrawide: number;
};

const FALLBACK_BREAKPOINTS: BreakpointMap = {
  mobileSm: 390,
  mobile: 480,
  tablet: 768,
  laptop: 1024,
  desktop: 1280,
  widescreen: 1440,
  ultrawide: 1920,
};

export type BreakpointName = keyof BreakpointMap;

const cssVarNames: Record<BreakpointName, string> = {
  mobileSm: "--bp-mobile-sm",
  mobile: "--bp-mobile",
  tablet: "--bp-tablet",
  laptop: "--bp-laptop",
  desktop: "--bp-desktop",
  widescreen: "--bp-widescreen",
  ultrawide: "--bp-ultrawide",
};

const cache = new Map<BreakpointName, number>();

/* QUERY HELPERS */
export const mediaQueryFrom = (name: BreakpointName): string =>
  `(min-width: ${getBreakpointValue(name)}px)`;

export const mediaQueryUpto = (name: BreakpointName): string => {
  const max = getBreakpointValue(name) - 1;
  return `(max-width: ${max}px)`;
};

export const mediaQueryBetween = (
  lower: BreakpointName,
  upper: BreakpointName
): string => {
  const min = getBreakpointValue(lower);
  const max = getBreakpointValue(upper) - 1;
  return `(min-width: ${min}px) and (max-width: ${max}px)`;
};

/* BREAKPOINTS */
const readBreakpointValue = (name: BreakpointName): number => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return FALLBACK_BREAKPOINTS[name];
  }
  const styles = window.getComputedStyle(document.documentElement);
  const raw = styles.getPropertyValue(cssVarNames[name]).trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : FALLBACK_BREAKPOINTS[name];
};

export const getBreakpointValue = (name: BreakpointName): number => {
  if (!cache.has(name)) {
    cache.set(name, readBreakpointValue(name));
  }
  return cache.get(name)!;
};

/* VIEWPORT GETTERS */
export const getViewportHeight = () => window.innerHeight;

export interface ExtendedViewportInformation {
  between: `${BreakpointName}-${BreakpointName}` | undefined;
  from: BreakpointName | undefined;
  upto: BreakpointName | undefined;
}
export const getExtendedViewportInfo = (): ExtendedViewportInformation => {
  const breakpoints: Array<BreakpointName> = Object.keys(
    FALLBACK_BREAKPOINTS
  ) as Array<BreakpointName>;
  let between: `${BreakpointName}-${BreakpointName}` | undefined;
  let from: BreakpointName | undefined;
  let upto: BreakpointName | undefined;
  const match = window.matchMedia;
  for (let i = 0; i < breakpoints.length; i++) {
    const bp = breakpoints[i] as BreakpointName;
    // Check highest from query
    if (match(mediaQueryFrom(bp)).matches) from = bp;
    // Check lowest possible to query
    if (!upto && match(mediaQueryUpto(bp)).matches) upto = bp;
    // Checks betweens
    if (i !== breakpoints.length - 1) {
      const bpTo = breakpoints[i + 1] as BreakpointName;
      const isBetween = match(mediaQueryBetween(bp, bpTo)).matches;
      if (isBetween) between = `${bp}-${bpTo}`;
    }
  }
  return { between, from, upto };
};

export const getViewportOrientation = (): ViewportOrientation => {
  if (
    window.matchMedia("(orientation: landscape)").matches &&
    window.innerWidth / window.innerHeight >= 19 / 6
  ) {
    return "landscape";
  }
  return "portrait";
};
